import { SelfServeNodes } from "@faire/help-center-self-serve/types";
import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Email } from "@faire/web--source/slate/icons/foundation/Email";
import { Exclusivity } from "@faire/web--source/slate/icons/foundation/Exclusivity";
import { ShoppingBag } from "@faire/web--source/slate/icons/foundation/ShoppingBag";
import React from "react";

import { BrandReferralCreditDiscrepancyIA } from "../instantAnswers/referrals-brandReferralsCreditDiscrepancy";
import { BrandReferralEarnedCreditIA } from "../instantAnswers/referrals-brandReferralsEarnCredit";
import { BrandReferralHowDoReferralsWorkIA } from "../instantAnswers/referrals-brandReferralsHowDoReferralsWork";
import { BrandReferralStatusIA } from "../instantAnswers/referrals-brandReferralsStatus";
import { SettingRetailerExclusivityIsOn } from "../visibilityFunctions/settingRetailerExclusivityVisibilityFn";

import { useInsiderRetailerNodes } from "./useInsiderRetailerNodes";

export const useFaireProgramsRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const insiders = useInsiderRetailerNodes(3);
  const articles = useArticles();
  return {
    FairePrograms: {
      id: "FairePrograms",
      label: strictLocalize({
        defaultMessage: "Faire programs",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Exclusivity fontSize="24px" color="faireBlack" />,
      children: ["Exclusivity", "OpenWithFaire", "ReferABrand", "Insider"],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Faire programs issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    Exclusivity: {
      id: "Exclusivity",
      label: strictLocalize({
        defaultMessage: "Exclusivity",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Exclusivity fontSize="24px" color="faireBlack" />,
      articles: [articles.Account.WHAT_IS_EXCLUSIVITY],
      isNodeVisible: SettingRetailerExclusivityIsOn,
      level: 2,
      sidebarTitle: strictLocalize({
        defaultMessage: "Exclusivity issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    OpenWithFaire: {
      id: "OpenWithFaire",
      label: strictLocalize({
        defaultMessage: "Open with Faire",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <ShoppingBag fontSize="24px" color="faireBlack" />,
      level: 1,
      articles: [articles.GettingStarted.OPEN_WITH_FAIRE],
      sidebarTitle: strictLocalize({
        defaultMessage: "Open with Faire issue",
        description: {
          text: "Title for displaying on the order summary sidebar",
        },
      }),
    },
    ReferABrand: {
      id: "ReferABrand",
      label: strictLocalize({
        defaultMessage: "Refer a brand",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <Email fontSize="24px" color="faireBlack" />,
      level: 2,
      sidebarTitle: strictLocalize({
        defaultMessage: "Refer a brand",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
      children: [
        "AboutBrandReferrals",
        "CheckReferralStatus",
        "EarnReferralCredit",
        "ReportCreditDiscrepancy",
      ],
    },
    AboutBrandReferrals: {
      id: "AboutBrandReferrals",
      label: strictLocalize({
        defaultMessage: "About brand referrals",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE,
        articles.FAQ.BRAND_REFERRAL_PROGRAM_TERMS_CONDITIONS,
      ],
      instantAnswer: BrandReferralHowDoReferralsWorkIA(strictLocalize),
      level: 3,
    },
    CheckReferralStatus: {
      id: "CheckReferralStatus",
      label: strictLocalize({
        defaultMessage: "Check referral status",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralStatusIA(strictLocalize),
      level: 3,
    },
    EarnReferralCredit: {
      id: "EarnReferralCredit",
      label: strictLocalize({
        defaultMessage: "Earn referral credit",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralEarnedCreditIA(strictLocalize),
      level: 3,
    },
    ReportCreditDiscrepancy: {
      id: "ReportCreditDiscrepancy",
      label: strictLocalize({
        defaultMessage: "Report credit discrepancy",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralCreditDiscrepancyIA(strictLocalize),
      level: 3,
    },
    ...insiders,
  };
};
